import axios from 'axios';
import { useEffect } from 'react';
import { useAuth } from '../context/AdminContext';

export const baseURL = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_URL_DEV : process.env.REACT_APP_API_URL;

const refreshTokenUrl = `/login/refresh`;

const Axios = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache'
  },
});

let failedQueue = [];
let isRefreshing = false;

/**
 * Funcion que procesa la cola de requests
 * @param errorRefreshing 
 */
const processQueue = (errorRefreshing) => {
  failedQueue.forEach((failedRequest) => {
    if (errorRefreshing) {
      failedRequest.reject(errorRefreshing);
    } else {
      failedRequest.resolve();
    }
  })
  failedQueue = [];
};

export function AxiosInterceptor({ children }) {

  const { logout } = useAuth();

  useEffect(() => {

    const reqInterceptor = config => {
      const token = localStorage.getItem('token');
      if (token && !config.url?.includes('refresh')) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    const failedResInterceptor = error => {
      const originalRequest = error.config;
      originalRequest.headers = JSON.parse(JSON.stringify(originalRequest.headers || {}));
      const refreshToken = localStorage.getItem('refreshToken');

      // Funcion que rechaza todas las promesas pendientes y desloguea al usuario
      const handleError = (error) => {
        processQueue(error);
        logout();
        return Promise.reject(error);
      };
      // Si hay un token de refresco, el token de acceso expiró y aun no se intento refrescar el token
      if (refreshToken && error.response?.status === 401 && error.response.data.message === "TokenExpiredError"
        && originalRequest?.url !== `${baseURL}${refreshTokenUrl}` && originalRequest?._retry !== true) {

        // Si otra request esta intentando refrescar el token, la request actual se agrega a la cola para ser resuelta una vez que se refresque
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          }).then(() => Axios(originalRequest))
            .catch((err) => Promise.reject(err));
        }
        isRefreshing = true;
        originalRequest._retry = true;

        return Axios.post(refreshTokenUrl, {}, { headers: { authorization: `Bearer ${refreshToken}` } })
          .then((res) => {
            if (res.data) {
              localStorage.setItem('token', res.data.token);
              localStorage.setItem('refreshToken', res.data.refreshToken);
            }
            processQueue(null);
            return Axios(originalRequest);
          }, handleError)
          .finally(() => {
            isRefreshing = false;
          });
      }
      // si llega a esta instancia es por que el token de refresco esta expirado o es inexistente
      if (error.response?.status === 401 && error.response?.data?.message === "TokenExpiredError") {
        return handleError(error);
      }
      return Promise.reject(error);
    }

    console.log('adding interceptors')
    const requestInterceptor = Axios.interceptors.request.use(reqInterceptor, error => { return Promise.reject(error); });
    const interceptor = Axios.interceptors.response.use(response => response, failedResInterceptor);

    return () => {
      console.log('removing interceptors')
      Axios.interceptors.request.eject(requestInterceptor);
      Axios.interceptors.response.eject(interceptor);
    }
  }, [])

  return children;
}

export default Axios;
